/**
 * 订单备注工具函数
 */

import i18n from '../i18n';

interface OrderNoteTranslation {
    translateNote?: string;
    lang?: string;
}

interface OrderNote {
    note?: string;
    lang?: string;
    orderNoteTranslations?: OrderNoteTranslation[];
}

/**
 * 获取当前系统登录的语言
 * 从i18n实例获取，优先使用resolvedLanguage，如果没有则使用language
 * @returns 当前语言（如 'zh', 'en', 'es'），默认返回 'en'
 */
function getCurrentLanguage(): string {
    try {
        // 从i18n实例获取当前语言
        // resolvedLanguage 是i18n检测并解析后的语言代码
        // language 是当前设置的语言代码
        const lang = i18n.resolvedLanguage || i18n.language || 'en';
        
        // 处理可能的格式如 'zh-CN'，只取前两位
        return lang.split('-')[0] || 'en';
    } catch (e) {
        // 如果i18n未初始化或获取失败，尝试从localStorage获取
        const storedLang = localStorage.getItem('i18nextLng');
        if (storedLang) {
            return storedLang.split('-')[0] || 'en';
        }
        
        // 默认返回英文
        return 'en';
    }
}

/**
 * 从订单备注对象中获取当前语言对应的备注内容
 * 
 * 逻辑：
 * 1. 判断orderNote.lang是否是当前登录所选的语言
 * 2. 如果是，直接返回note
 * 3. 如果不是，则从orderNoteTranslations中找匹配语言的translateNote
 * 4. 如果没有符合的，返回外层的note
 * 
 * @param orderNote 订单备注对象
 * @returns 当前语言对应的备注内容，如果没有则返回原始note
 */
export function getOrderNoteText(
    orderNote: OrderNote | null | undefined
): string {
    if (!orderNote || !orderNote.note) {
        return '';
    }

    // 获取当前系统登录的语言
    const currentLanguage = getCurrentLanguage();

    // 判断orderNote.lang是否是当前语言
    if (orderNote.lang === currentLanguage) {
        return orderNote.note;
    }

    // 如果不是当前语言，则从orderNoteTranslations中找匹配语言的translateNote
    if (orderNote.orderNoteTranslations && orderNote.orderNoteTranslations.length > 0) {
        const translation = orderNote.orderNoteTranslations.find(
            (t) => t.lang === currentLanguage
        );
        
        if (translation?.translateNote) {
            return translation.translateNote;
        }
    }

    // 如果没有符合的，返回外层的note
    return orderNote.note;
}

